import {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react'

import classNames from 'classnames/bind'

import {useTraceContext} from '../../trace/TraceProvider'

import Empty from './Empty'
import Item from './Item'
import Toolbar from './Toolbar'
import styles from './TraceContent.module.scss'

import type {TraceOptions} from '../../trace/type'
import type {FC} from 'react'

const SCROLL_THRESHOLD = 50

const cx = classNames.bind(styles)

const TraceContent: FC<TraceOptions> = ({filterScope}) => {
    const {traces} = useTraceContext()
    const [searchInput, setSearchInput] = useState('')

    // 새로운 trace가 추가될 때마다 스크롤이 맨 아래에 있는지 확인하기 위한 ref
    const isAtBottomRef = useRef(true)
    const listRef = useRef<HTMLUListElement>(null)

    /**
     * 검색에 기반해 사용자에게 보여 줄 trace 목록을 계산합니다.
     * 1. filterScope이 제공된 경우, scope 기준으로 trace를 필터링합니다.
     * 2. searchInput가 있는 경우, scope과 message를 대상으로 검색어가 포함된 trace를 필터링합니다.
     */
    const filteredTraces = useMemo(() => {
        const scopeFiltered = filterScope ? traces.filter((trace) => filterScope(trace.scope)) : traces
        if (!searchInput) {
            return scopeFiltered
        }
        const input = searchInput.toLowerCase().trim()

        return scopeFiltered.filter(
            (trace) =>
                trace.scope.toLowerCase().trim().includes(input) ||
                trace.message.some((node) => node.preview.toLowerCase().trim().includes(input)),
        )
    }, [traces, filterScope, searchInput])

    const onSearchInputChange = useCallback((value: string) => {
        setSearchInput(value)
    }, [])

    const onScroll = useCallback(() => {
        const el = listRef.current
        if (!el) {
            return
        }
        isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD
    }, [])

    // filteredTraces가 변경될 때마다 스크롤이 맨 아래에 있는 경우에만 자동으로 스크롤을 맨 아래로 이동 (오차 범위 내)
    useLayoutEffect(() => {
        const el = listRef.current
        if (!el || !isAtBottomRef.current) {
            return
        }
        el.scrollTo({top: el.scrollHeight, behavior: 'instant'})
    }, [filteredTraces])

    return (
        <div className={cx('container')}>
            <Toolbar
                count={filteredTraces.length}
                searchInput={searchInput}
                onSearchInputChange={onSearchInputChange}
            />

            {filteredTraces.length === 0 ? (
                <Empty />
            ) : (
                <ul ref={listRef} onScroll={onScroll} className={cx('list')}>
                    {filteredTraces.map((trace) => (
                        <Item key={trace.id} trace={trace} />
                    ))}
                </ul>
            )}
        </div>
    )
}

export default TraceContent
